/**
 * es-MX — the SOURCE language (§1.2). Informal tú, verbs-first CTAs, craft language.
 * en-US mirrors this shape exactly (its type is derived from this object).
 * Never the forbidden acronym: cuts are "detectados automáticamente", art is the "estudio de portadas".
 */
export const esMX = {
  locale: 'es-MX',
  localeName: 'Español',
  otherLocaleName: 'English',

  nav: {
    bench: 'Banco',
    studio: 'Estudio',
    vocal: 'Voz',
    packs: 'Packs',
    submissions: 'Propuestas',
    market: 'Mercado',
    directory: 'Directorio',
    purchases: 'Compras',
    account: 'Cuenta',
    upload: 'Subir',
  },

  common: {
    brand: 'DJMEXXICO',
    tagline: 'Cortes exclusivos',
    loading: 'Cargando…',
    loadingMore: 'Cargando más…',
    retry: 'Reintentar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    back: 'Volver',
    save: 'Guardar',
    saving: 'Guardando…',
    confirm: 'Confirmar',
    delete: 'Eliminar',
    signOut: 'Cerrar sesión',
    of: 'de',
    free: 'Gratis',
    required: 'obligatorio',
  },

  errors: {
    generic: 'Algo falló. Intenta de nuevo.',
    network: 'Sin conexión. Revisa tu red.',
    rateLimited: (secs: number) => `Demasiadas solicitudes. Reintenta en ${secs}s.`,
    unauthorized: 'Tu sesión expiró. Inicia sesión otra vez.',
    notFound: 'No encontramos eso.',
    tooLarge: 'El archivo pasa el límite de tu plan.',
    unsupported: 'Formato no soportado. Usa WAV, AIFF o MP3.',
    fieldRequired: 'Este campo es obligatorio.',
    badEmail: 'Correo no válido.',
    shortPassword: 'Mínimo 8 caracteres.',
  },

  states: {
    pending: 'EN COLA',
    processing: 'PROCESANDO',
    review: 'REVISA CORTES',
    complete: 'LISTO',
    failed: 'FALLÓ',
    sold: 'VENDIDO',
    draft: 'BORRADOR',
    published: 'PUBLICADO',
  },

  landing: {
    heroKicker: 'CDMX · UNDERGROUND · PRODUCTOR',
    heroTitle: 'Sube tu beat.\nCorta el exclusivo.',
    heroBody:
      'DJMEXXICO corta tu sonido en exclusivos. Sube un beat crudo, la máquina encuentra los cortes, tú los revisas, y en minutos tienes un sample pack listo para vender.',
    ctaPrimary: 'Crear cuenta',
    ctaSecondary: 'Ya tengo cuenta',
    step1Title: 'Sube',
    step1Body: 'Arrastra tu WAV, AIFF o MP3. Hasta 200 MB. El corte empieza solo.',
    step2Title: 'Revisa los cortes',
    step2Body: 'Los cortes se detectan automáticamente. Ajústalos con precisión de milisegundo y confírmalos.',
    step3Title: 'Publica y cobra',
    step3Body: 'Arma tu pack, ponle precio en USD y publícalo en el mercado. El pago llega directo.',
    machineShowsWork: 'La máquina muestra su trabajo: estados, tiempos, tamaños. Nada de girar y rezar.',
    footerRights: 'Cortes exclusivos para el under.',
  },

  auth: {
    signInTitle: 'Entra a la sala de corte',
    signUpTitle: 'Abre tu sala de corte',
    signInSubtitle: 'Tu sesión, tus cortes, tu catálogo.',
    signUpSubtitle: 'Crea tu cuenta y sube tu primer beat.',
    nameLabel: 'Nombre',
    namePlaceholder: 'Tu nombre de productor',
    emailLabel: 'Correo',
    passwordLabel: 'Contraseña',
    passwordHint: 'Mínimo 8 caracteres.',
    signInCta: 'Entrar',
    signUpCta: 'Crear cuenta',
    toSignUp: '¿No tienes cuenta? Créala',
    toSignIn: '¿Ya tienes cuenta? Entra',
    working: 'Verificando…',
  },

  upload: {
    title: 'Estudio de subida',
    subtitle: 'Sube un beat y la máquina encuentra los cortes.',
    dropTitle: 'Suelta tu beat aquí',
    dropHint: 'o toca para buscar · WAV, AIFF, MP3 · máx 200 MB',
    browse: 'Buscar archivo',
    uploading: 'Subiendo',
    processing: 'Registrando el corte…',
    cancel: 'Cancelar subida',
    successTitle: 'Beat en la línea',
    successBody: 'Tu beat entró. Te avisamos cuando los cortes estén listos para revisar.',
    goToReview: 'Ir a revisar cortes',
    another: 'Subir otro',
    ofSize: (done: string, total: string) => `${done} de ${total}`,
  },

  review: {
    title: 'Revisa los cortes',
    subtitle: 'Ajusta y confirma antes de renderizar el pack.',
    waitingTitle: 'Encontrando los cortes',
    waitingBody: 'Esto tarda un momento. La máquina analiza tu beat y marca los puntos de corte.',
    countLabel: (total: number, confirmed: number) => `${total} cortes · ${confirmed} confirmados`,
    confirmCta: (confirmed: number, total: number) => `Confirmar cortes (${confirmed}/${total})`,
    rendering: 'Renderizando pack…',
    renderedTitle: 'Pack renderizado',
    renderedBody: 'Tus cortes quedaron. El pack está listo en tu biblioteca.',
    goToLibrary: 'Ver en biblioteca',
    marker: {
      detected: 'detectado',
      adjusted: 'ajustado',
      confirmed: 'confirmado',
    },
    toolbar: {
      nudgeBack: '−10 ms',
      nudgeFwd: '+10 ms',
      playSeg: 'Reproducir segmento',
      confirmOne: 'Confirmar corte',
      deleteOne: 'Eliminar corte',
      addMarker: 'Agregar corte',
    },
    ariaMarker: (n: number, time: string, state: string) => `Corte ${n} — ${time}, ${state}`,
    announceConfirmed: (n: number) => `Corte ${n} confirmado`,
    announceAdjusted: (n: number, time: string) => `Corte ${n} movido a ${time}`,
    keysTitle: 'Atajos de teclado',
    keys: [
      ['Tab', 'Recorrer cortes'],
      ['← / →', 'Mover 10 ms'],
      ['Shift + ← / →', 'Mover 1 ms (fino)'],
      ['Ctrl + ← / →', 'Mover 100 ms (grueso)'],
      ['Enter', 'Confirmar corte'],
      ['Supr', 'Eliminar corte'],
      ['Espacio', 'Reproducir / pausar segmento'],
      ['A', 'Agregar corte en la posición'],
      ['Esc', 'Deseleccionar'],
    ] as [string, string][],
    failedTitle: 'El corte falló con este beat',
    failedBody: 'Algo salió mal al procesar. Puedes reintentar la subida.',
  },


  bench: {
    title: 'Tu banco',
    subtitle: 'Dónde está tu sonido, y qué sigue.',
    yourMove: 'Tu jugada',
    dig: 'Excava',
    digVerb: 'Tráelo',
    chop: 'Corta',
    chopVerb: 'Córtalo',
    pack: 'Empaca',
    packVerb: 'En el banco',
    ship: 'Envía',
    shipVerb: 'Véndelo',
    move: {
      dig: {
        title: 'Trae un beat.',
        body: 'Nada en el banco todavía. Suelta un beat crudo y arranca el corte.',
        cta: 'Traer un beat',
      },
      chop: {
        title: 'Corta tu beat.',
        body: (title: string) => `${title} está rebanado y espera tu decisión. Ajusta los cortes y confírmalos.`,
        cta: 'Cortarlo',
      },
      ship: {
        title: 'Envía tu pack.',
        body: (title: string) => `${title} está empacado y listo. Ponle precio, publícalo, cobra directo.`,
        cta: 'Enviarlo',
      },
      wait: {
        title: 'El banco está trabajando.',
        body: (n: number) => `${n} ${n === 1 ? 'beat está' : 'beats están'} en la línea. Cortes, render, pack.`,
        cta: 'Nada que hacer todavía — el banco muestra su trabajo.',
      },
    },
    failed: (n: number) => `${n} ${n === 1 ? 'beat' : 'beats'} que el banco no pudo terminar.`,
    failedCta: 'Abrir biblioteca',
    unresumable: (n: number) =>
      `${n} ${n === 1 ? 'corte más antiguo espera' : 'cortes más antiguos esperan'} pero no se pueden reabrir desde aquí. Búscalos en tu biblioteca.`,
  },
  library: {
    title: 'Tu biblioteca',
    subtitle: 'Todo lo que has cortado.',
    emptyTitle: 'Nada cortado todavía',
    emptyBody: 'Sube tu primer beat y arranca el corte.',
    emptyCta: 'Subir beat',
    downloadPack: 'Descargar pack',
    downloadPreview: 'Escuchar preview',
    createListing: 'Publicar en el mercado',
    reviewCuts: 'Revisar cortes',
    showProvenance: 'Ver procedencia',
    hideProvenance: 'Ocultar procedencia',
    meta: (dur: string, bpm: string, fmt: string) => `${dur} · ${bpm} · ${fmt}`,
  },

  listing: {
    title: 'Publica tu pack',
    subtitle: 'Arma el anuncio y ponle precio.',
    fromAsset: 'Desde tu asset',
    titleLabel: 'Título del pack',
    titlePlaceholder: 'p. ej. Cumbia Break 140',
    descLabel: 'Descripción',
    descPlaceholder: 'Cuenta qué trae el pack, cómo suena, para quién es.',
    priceLabel: 'Precio',
    licenseLabel: 'Licencia',
    license: {
      royalty_free: 'Libre de regalías',
      exclusive: 'Exclusiva',
      sync: 'Sincronización',
      wellness: 'Bienestar',
    },
    memberOnly: 'Solo para miembros',
    publishCta: 'Publicar pack',
    draftCta: 'Guardar borrador',
    publishing: 'Publicando…',
    publishedTitle: 'Pack publicado',
    publishedBody: 'Tu pack ya está en el mercado. Te avisamos con cada venta.',
    viewInMarket: 'Ver en el mercado',
    draftSavedTitle: 'Borrador guardado',
    draftSavedBody: 'Todavía no está en el mercado. Publícalo cuando el pack esté listo.',
  },

  market: {
    title: 'Mercado',
    subtitle: 'Packs de la comunidad, listos para tu próxima sesión.',
    searchPlaceholder: 'Busca por nombre, género, vibra…',
    resultCount: (n: number) => `${n} resultados`,
    filterAll: 'Todo',
    sortRecent: 'Recientes',
    sortRelevance: 'Relevancia',
    filterKind: 'Tipo',
    filterLicense: 'Licencia',
    filterBpm: 'BPM',
    filterKey: 'Tonalidad',
    keyAny: 'Cualquier',
    emptyTitle: 'Nada por aquí… todavía',
    emptyBody: 'Ningún pack coincide con tu búsqueda. Prueba con otros filtros.',
    buy: 'Comprar',
    preview: 'Preview',
    pause: 'Pausa',
    previewUnavailable: 'Aún sin preview',
    detailMeta: (samples: string, fmt: string, bpm: string) => `${samples} · ${fmt} · ${bpm}`,
    licenseLine: (lic: string) => `Licencia: ${lic}`,
    byCreator: 'Del productor',
    checkoutOpening: 'Abriendo pago seguro…',
    checkoutSuccess: 'Compra confirmada. Revisa tu correo para la descarga.',
    checkoutCancel: 'Pago cancelado.',
  },

  creator: {
    subtitle: 'Tienda del productor',
    packCount: (n: number) => `${n} packs`,
    cosigns: (n: number) => `${n} ${n === 1 ? 'respaldo' : 'respaldos'}`,
    notFoundTitle: 'Productor no encontrado',
    notFoundBody: 'Esta tienda no existe o aún no tiene packs publicados.',
    emptyTitle: 'Aún sin packs',
    emptyBody: 'Este productor todavía no ha publicado ningún pack.',
  },

  account: {
    title: 'Cuenta',
    language: 'Idioma',
    session: 'Sesión activa',
    signedInAs: (email: string) => `Sesión iniciada como ${email}`,
  },

  payouts: {
    title: 'Cobros',
    checking: 'Consultando el estado de tus pagos…',
    notStarted: 'Todavía no configuras cómo cobrar. Conecta tu cuenta y tus ventas se depositan directo.',
    inProgress: 'Configuración en proceso. Faltan pasos por completar.',
    restricted: 'Tu cuenta de pagos necesita atención. Completa los datos pendientes.',
    active: 'Pagos activos. Cada venta se deposita directo a tu cuenta.',
    setupCta: 'Configurar cobros',
    resumeCta: 'Continuar configuración',
    dashboardCta: 'Abrir panel de cobros',
    opening: 'Abriendo…',
    stateLabel: 'Estado',
    stateNotStarted: 'SIN CONFIGURAR',
    stateInProgress: 'EN PROCESO',
    stateRestricted: 'REQUIERE ATENCIÓN',
    stateActive: 'ACTIVO',
    composerNotice: 'Para cobrar lo que vendas, primero configura tus cobros. Puedes guardar borradores mientras tanto.',
    composerCta: 'Configura tus cobros primero',
  },

  credits: {
    title: 'Créditos',
    balanceLabel: 'Saldo',
    balance: (n: number) => `${n} ${n === 1 ? 'crédito' : 'créditos'}`,
    packsLabel: 'Recargas',
    packCta: (credits: number, price: string) => `${credits} créditos · ${price}`,
    opening: 'Abriendo pago seguro…',
    success: 'Pago confirmado. Tus créditos ya están en tu saldo.',
    cancel: 'Pago cancelado.',
  },

  purchases: {
    title: 'Compras',
    subtitle: 'Tus licencias y sus descargas.',
    emptyTitle: 'Sin compras todavía',
    emptyBody: 'Cuando compres un pack en el mercado, su licencia y descarga aparecen aquí.',
    emptyCta: 'Ir al mercado',
    download: 'Descargar',
    viewCertificate: 'Ver certificado',
    downloadsLeft: (n: number) => `${n} ${n === 1 ? 'descarga restante' : 'descargas restantes'}`,
    unlimited: 'Descargas ilimitadas',
    licenseLine: (lic: string) => `Licencia: ${lic}`,
    stateRevoked: 'REVOCADA',
    stateExpired: 'EXPIRADA',
    stateExhausted: 'SIN DESCARGAS',
  },

  bloodline: {
    minted: 'LINAJE ACUÑADO',
    source: 'ORIGEN',
    thisPack: 'ESTE PACK',
    derivedBody: (hops: number) =>
      `Cada comprador recibe esta cadena de custodia con el pack — ${hops} ${hops === 1 ? 'salto' : 'saltos'} hasta el origen. Viaja con el sonido.`,
    sourceBody:
      'Este pack es el origen. Todo lo que se corte de él lleva este nodo, y cada comprador recibe la cadena con el sonido.',
  },

  cosign: {
    count: (n: number) => `${n} ${n === 1 ? 'productor ha' : 'productores han'} respaldado este pack`,
    none: 'Aún sin respaldos',
    action: 'Respalda este pack',
    done: 'Ya lo respaldaste',
    ownPack: 'Tu pack — otros lo respaldan, tú no.',
    explain: 'Pon tu nombre. Tu respaldo lleva el prestigio que te ganaste.',
    signInPrompt: 'Inicia sesión para respaldar',
  },

  provenance: {
    title: 'Cadena de custodia',
    loading: 'Resolviendo procedencia…',
    originalRoot: 'Original sin derivaciones — es la raíz de su propia cadena de custodia.',
    thisAsset: 'ESTE ASSET',
    hops: (n: number) => `${n} ${n === 1 ? 'paso' : 'pasos'}`,
  },

  vocal: {
    title: 'Estudio de voz',
    subtitle: 'Procesa tus tomas: rango, afinación y acapella lista para vender.',
    profileTitle: 'Tu rango vocal',
    profileEmpty: 'Aún no procesas ninguna toma. Procesa una para construir tu perfil vocal.',
    rangeLabel: 'Rango',
    meanLabel: 'Tono medio',
    keyLabel: 'Tonalidad sugerida',
    takesLabel: (n: number) => `${n} ${n === 1 ? 'toma procesada' : 'tomas procesadas'}`,
    sourcesTitle: 'Tomas listas para procesar',
    sourcesEmpty: 'No tienes audio listo. Sube una toma en el estudio.',
    processCta: 'Procesar voz · 3 créditos',
    processingCta: 'Procesando…',
    jobQueued: 'Toma en la línea. La máquina separa y afina tu voz.',
    jobRunning: 'Procesando tu voz: rango, respiraciones y nivel.',
    jobDone: 'Acapella lista. La agregamos a tus packs.',
    jobFailed: 'El procesamiento falló. Intenta de nuevo.',
    packsTitle: 'Tus acapellas',
    packsEmpty: 'Cuando proceses una toma, tu acapella aparece aquí.',
    publishCta: 'Publicar',
    submitCta: 'Enviar a productor',
    goUpload: 'Ir al estudio',
    hz: (n: string) => `${n} Hz`,
  },

  submissions: {
    title: 'Propuestas',
    subtitle: 'Envía tus acapellas a productores y responde las que recibes.',
    inbox: 'Recibidas',
    outbox: 'Enviadas',
    emptyInbox: 'Sin propuestas por ahora. Cuando alguien te envíe una acapella, aparece aquí.',
    emptyOutbox: 'No has enviado propuestas. Envía una acapella desde el estudio de voz.',
    accept: 'Aceptar',
    decline: 'Rechazar',
    withdraw: 'Retirar',
    fromLabel: 'De',
    toLabel: 'Para',
    statusPending: 'PENDIENTE',
    statusAccepted: 'ACEPTADA',
    statusDeclined: 'RECHAZADA',
    statusWithdrawn: 'RETIRADA',
    composeTitle: 'Enviar propuesta',
    composeBody: 'Comparte esta acapella con un productor. Necesitas su identificador de usuario.',
    recipientLabel: 'Identificador del productor',
    recipientHelp: 'Pega el identificador de usuario del productor que recibirá la propuesta.',
    messageLabel: 'Mensaje (opcional)',
    messagePlaceholder: 'Cuéntale de qué va la toma…',
    send: 'Enviar propuesta',
    sending: 'Enviando…',
    sent: 'Propuesta enviada.',
    accepted: 'Propuesta aceptada.',
    declined: 'Propuesta rechazada.',
    withdrawn: 'Propuesta retirada.',
  },

  directory: {
    title: 'Directorio',
    subtitle: 'Explora a cada creador con catálogo publicado.',
    sortRecent: 'Recientes',
    sortActive: 'Más activos',
    sortAlphabetical: 'A–Z',
    resultCount: (n: number) => `${n} creadores`,
    packsCount: (n: number) => (n === 1 ? '1 pack' : `${n} packs`),
    viewStorefront: 'Ver tienda',
    emptyTitle: 'Aún no hay creadores',
    emptyBody: 'Cuando alguien publique un pack, aparecerá aquí.',
  },

  onboarding: {
    welcomeTitle: 'Bienvenido a la sala de corte',
    welcomeBody: (credits: number) =>
      `Tu cuenta empieza con ${credits} créditos para tu primer pack. Preparemos tu perfil.`,
    stepOf: (current: number, total: number) => `Paso ${current} de ${total}`,
    handleStepTitle: 'Elige tu identificador',
    handleStepBody: 'Así te encuentran en el directorio y en tu tienda.',
    handleLabel: 'Identificador',
    handlePlaceholder: 'tu-nombre',
    handleHelp: '3–32 caracteres: minúsculas, números y guiones interiores.',
    displayNameLabel: 'Nombre público',
    displayNamePlaceholder: 'Tu nombre de artista',
    uploadStepTitle: 'Sube tu primer pack',
    uploadStepBody: 'Lleva tu sonido al estudio y publícalo en el mercado.',
    goToStudio: 'Ir al estudio',
    next: 'Siguiente',
    back: 'Atrás',
    skip: 'Omitir por ahora',
    finish: 'Listo',
    saving: 'Guardando…',
    saved: 'Perfil guardado.',
    handleTaken: 'Ese identificador ya está tomado.',
    handleInvalid: '3–32 caracteres: minúsculas, números y guiones interiores.',
  },
};

// Derived from the es-MX source; string literals widen to `string` so en-US can mirror it.
export type Dictionary = typeof esMX;
